import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, Download } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import EditableText from './cms/EditableText';

const Footer = () => {
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const { t } = useLanguage();

  const services = [
    t('footer.aiSolutions'),
    t('footer.webSeo'), 
    t('footer.itConsulting'),
    t('footer.softwareDevelopment')
  ];

  const navItems = [
    { key: 'services', label: t('nav.services') },
    { key: 'about', label: t('footer.about') },
    { key: 'references', label: t('footer.references') },
    { key: 'contact', label: t('footer.contact') }
  ];

  const handlePrivacyPolicyDownload = () => {
    const link = document.createElement('a');
    link.href = '/commodIT_PrivacyPolicy_hu.pdf';
    link.download = 'commodIT_PrivacyPolicy_hu.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowPrivacyPolicy(false);
  };

  return (
    <footer className="relative bg-background border-t border-border">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-64 h-64 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute top-16 -right-16 w-48 h-48 bg-accent/10 rounded-full blur-2xl"></div>
        <div className="absolute bottom-0 left-1/3 w-32 h-32 bg-secondary/10 rounded-full blur-xl"></div>
      </div>

      <div className="relative container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="md:col-span-1">
            <div className="mb-6">
              <span className="text-2xl font-display font-bold gradient-text">
                commodIT
              </span>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              <EditableText k="footer.transformingBusinesses" />
            </p>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-foreground mb-4"><EditableText k="footer.services" /></h3>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service}>
                  <a 
                    href="#services"
                    className="text-muted-foreground hover:text-primary transition-smooth text-sm"
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="font-semibold text-foreground mb-4"><EditableText k="footer.navigation" /></h3>
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.key}>
                  <a 
                    href={`#${item.key}`}
                    className="text-muted-foreground hover:text-primary transition-smooth text-sm"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold text-foreground mb-4"><EditableText k="footer.legal" /></h3>
            <div className="space-y-2">
              <div className="relative">
                <button
                  onClick={() => setShowPrivacyPolicy(!showPrivacyPolicy)}
                  className="flex items-center text-muted-foreground hover:text-primary transition-smooth text-sm"
                >
                  <EditableText k="footer.privacyPolicy" />
                  <ChevronDown className={`ml-1 w-3 h-3 transition-transform ${showPrivacyPolicy ? 'rotate-180' : ''}`} />
                </button>
                
                {showPrivacyPolicy && (
                  <div className="absolute top-full left-0 mt-2 bg-card border border-border rounded-lg p-3 shadow-lg z-10">
                    <Button
                      onClick={handlePrivacyPolicyDownload}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2 text-xs"
                    >
                      <Download className="w-3 h-3" />
                      <EditableText k="footer.downloadPdf" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border mt-12 pt-8">
          <div className="text-center">
            <p className="text-muted-foreground text-sm">
              <EditableText k="footer.allRightsReserved" />
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;